import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { planningExceptionListColumns } from "@patronix/domain/list";
import type { ListTools } from "@/components/ListToolbar";
import { DataList, type Column } from "@/components/DataList";
import { Badge } from "@/components/ui";
import {
  coverageQuery,
  exceptionKinds,
  exceptionListQuery,
  listExceptions,
  type ExceptionKind,
  type ExceptionRow,
} from "@/features/planning/api";
import { RunBar } from "@/features/planning/RunBar";
import { validateExceptionSearch, type ExceptionSearch } from "@/features/planning/search";
import { activeCompanyOf, useActiveCompany } from "@/lib/activeCompany";
import { cn } from "@/lib/cn";
import { EXPORT_PAGE, type ExportColumn } from "@/lib/exportXlsx";
import { useFormat } from "@/lib/format";
import { listView, viewQuery } from "@/lib/listView";
import { sessionContextQuery } from "@/lib/session";

// MRP: run status and the exceptions of the latest run (planning_exception_page,
// keyset on need date). A row opens the item's stock coverage.

export const Route = createFileRoute("/_app/planning/mrp")({
  validateSearch: validateExceptionSearch,
  loaderDeps: ({ search }) => ({ kind: search.kind, f: search.f, s: search.s }),
  loader: async ({ context, deps }) => {
    const ctx = await context.queryClient.ensureQueryData(sessionContextQuery);
    const companyId = activeCompanyOf(ctx)?.id;
    if (companyId) await context.queryClient.ensureInfiniteQueryData(exceptionListQuery({ companyId, kind: deps.kind, ...viewQuery(planningExceptionListColumns, deps) }));
  },
  component: MrpPage,
});

const tone: Record<ExceptionKind, "danger" | "warning" | "info" | "neutral"> = {
  expedite: "danger",
  start_in_past: "danger",
  demand_in_past: "danger",
  below_safety: "warning",
  no_bom: "warning",
  postpone: "info",
  excess: "neutral",
};

function MrpPage() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const { company } = useActiveCompany();
  const companyId = company?.id ?? "";
  const setSearch = useCallback(
    (patch: Partial<ExceptionSearch>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const params = { companyId, kind: search.kind, ...viewQuery(planningExceptionListColumns, search) };
  const query = useInfiniteQuery(exceptionListQuery(params));
  const tr = t as unknown as (k: string) => string;
  const source = (r: ExceptionRow) => (r.source_type ? (r.source_number ?? tr(`plan.sources.${r.source_type}`)) : "—");

  const columns: Column<ExceptionRow>[] = [
    {
      key: "kind",
      header: t("plan.colException"),
      width: "minmax(150px,1fr)",
      cell: (r) => <Badge tone={tone[r.kind]}>{t(`plan.exceptionKinds.${r.kind}`)}</Badge>,
      options: exceptionKinds.map((k) => ({ value: k, label: t(`plan.exceptionKinds.${k}`) })),
    },
    {
      key: "item",
      header: t("bom.colItem"),
      width: "minmax(200px,1.6fr)",
      cell: (r) => (
        <span className="truncate">
          <span className="font-mono text-xs">{r.item_code}</span> <span className="text-ink-soft">{r.item_description}</span>
        </span>
      ),
    },
    {
      key: "source",
      header: t("plan.colSource"),
      width: "minmax(130px,1fr)",
      cell: (r) => <span className="font-mono text-xs">{source(r)}</span>,
    },
    {
      key: "need",
      header: t("plan.colNeed"),
      width: "minmax(100px,0.8fr)",
      cell: (r) => <span className="tabular">{r.need_date ? fmt.day(r.need_date) : "—"}</span>,
    },
    {
      key: "supply",
      header: t("plan.colSupply"),
      width: "minmax(100px,0.8fr)",
      cell: (r) => <span className="tabular text-ink-muted">{r.supply_date ? fmt.day(r.supply_date) : "—"}</span>,
    },
    {
      key: "qty",
      header: t("prd.quantity"),
      width: "minmax(110px,0.8fr)",
      align: "end",
      cell: (r) =>
        r.qty === null ? (
          "—"
        ) : (
          <span className="tabular">
            {fmt.qtyIn(r.qty, r.base_uom_decimals)} <span className="text-ink-muted">{r.base_uom_code}</span>
          </span>
        ),
    },
  ];
  const exportColumns: ExportColumn<ExceptionRow>[] = [
    { header: t("plan.colException"), kind: "code", value: (r) => r.kind },
    { header: t("bom.colItem"), kind: "code", value: (r) => r.item_code },
    { header: t("stock.colDescription"), kind: "text", value: (r) => r.item_description },
    { header: t("plan.colSource"), kind: "code", value: (r) => r.source_number },
    { header: t("plan.colNeed"), kind: "date", value: (r) => r.need_date },
    { header: t("plan.colSupply"), kind: "date", value: (r) => r.supply_date },
    { header: t("prd.quantity"), kind: "number", value: (r) => r.qty, unit: (r) => r.base_uom_code },
    { header: t("items.colUom"), kind: "code", value: (r) => r.base_uom_code },
  ];
  const chip = (active: boolean) =>
    cn(
      "h-8 rounded-full border px-3 text-[13px] transition-colors",
      active ? "border-ink bg-ink font-medium text-surface" : "border-border bg-surface text-ink hover:bg-surface-hover",
    );

  const listTools: ListTools<ExceptionRow> = {
    id: "mrp-exceptions",
    title: t("plan.exceptions"),
    export: { fileBase: "mrp-exceptions", sheet: t("plan.exceptions"), columns: exportColumns, fetchPage: (cursor, signal) => listExceptions(params, cursor, signal, EXPORT_PAGE) },
  };

  return (
    <div className="flex min-h-0 grow flex-col gap-4">
      <header className="flex flex-wrap items-end gap-3">
        <div className="flex grow flex-col gap-0.5">
          <nav aria-label="Breadcrumb" className="text-[13px] text-ink-muted">
            {t("nav.planning.title")} <span aria-hidden="true">/</span> {t("nav.planning.mrp")}
          </nav>
          <h1 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">{t("plan.mrpTitle")}</h1>
        </div>
      </header>
      <RunBar companyId={companyId} />
      <div role="group" aria-label={t("plan.colException")} className="flex flex-wrap gap-2">
        <button
          type="button"
          aria-pressed={!search.kind}
          onClick={() => setSearch({ kind: undefined })}
          className={chip(!search.kind)}
        >
          {t("plan.allExceptions")}
        </button>
        {exceptionKinds.map((k) => (
          <button
            key={k}
            type="button"
            aria-pressed={search.kind === k}
            onClick={() => setSearch({ kind: k })}
            className={chip(search.kind === k)}
          >
            {t(`plan.exceptionKinds.${k}`)}
          </button>
        ))}
      </div>
      <div className="flex min-h-0 grow">
        <DataList
          label={t("plan.exceptionListLabel")}
          columns={columns}
          query={query}
          rowId={(r) => r.id}
          selectedId={undefined}
          onSelect={(r) => void navigate({ to: "/planning/stock-coverage", search: { item: r.item_id } })}
          onClear={() => undefined}
          onPrefetch={(r) => void queryClient.prefetchQuery(coverageQuery(companyId, r.item_id))}
          emptyTitle={t("plan.exceptionsEmptyTitle")}
          emptyBody={t("plan.exceptionsEmptyBody")}
          minWidth={820}
          tools={listTools}
          view={listView(planningExceptionListColumns, search, setSearch)}
        />
      </div>
    </div>
  );
}
